import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Container,
  Flex,
  HStack,
  Icon,
  IconButton,
  SimpleGrid,
  Text,
  VStack,
  useColorMode,
} from "@chakra-ui/react";
import {
  FiChevronDown,
  FiChevronUp,
  FiCheck,
  FiEdit2,
  FiEye,
  FiEyeOff,
  FiGrid,
  FiRotateCcw,
} from "react-icons/fi";
import { useMutation } from "@apollo/client";
import { useAuth } from "../contexts/AuthContext";
import { allModuleConfigs, moduleIcons } from "./FloatingNavbar";
import { SAVE_MY_QUICKLINKS } from "../pages/clients/queries";
import { ModuleConfig } from "../types/ModuleConfig";

// Some module configs store an emoji in `icon` (e.g. "👥"), others store a
// react-icons component name (e.g. "FiMessageSquare"). Render the actual icon
// for the latter — via the shared moduleIcons id→component map — instead of
// printing the raw name string.
const ModuleGlyph = ({ config }: { config: ModuleConfig }) => {
  const isIconName = /^Fi[A-Z]/.test(config.icon || "");
  if (isIconName) {
    const Comp = moduleIcons[config.id] || FiGrid;
    return <Icon as={Comp} boxSize={5} />;
  }
  return (
    <Text fontSize="xl" lineHeight="1">
      {config.icon}
    </Text>
  );
};

/**
 * AdminModuleLinks
 *
 * A quick-jump grid of every enabled module, shown at the TOP of the home page
 * ONLY to a logged-in ADMIN. Each card opens its module in a NEW TAB.
 *
 * Curatable per-person (mobile-first), persisted to the CLIENT RECORD so it
 * follows the user across devices (phone ↔ laptop):
 *  - Edit mode turns each card into a tile with move ↑/↓ (reorder) and an eye
 *    toggle (hide/show). Big tap targets — no drag-and-drop, so it works on a
 *    phone where this is normally edited. Edit/Reset live at the BOTTOM.
 *  - Order + hidden set are seeded from `myClient.preferences.quickLinks` at
 *    login and saved via the `saveMyQuickLinks` mutation (debounced, optimistic).
 *  - Until the person has curated it, the grid is ALPHABETICAL (A–Z by module
 *    name) — never `config.order`, which nobody chose. Reset returns to A–Z.
 *  - Cross-device is eventual: another device sees changes on its next auth read.
 *
 * Self-gating: renders null unless `isAuthenticated && user.permissions.includes("ADMIN")`.
 * "ADMIN" is the canonical admin permission string in this app (AdminDashboard.tsx / P2 / calendars).
 * Reuses the single canonical `allModuleConfigs` list from FloatingNavbar (no divergent copy).
 */

interface QuickLinkPrefs {
  order: string[]; // module ids, in the user's chosen order
  hidden: string[]; // module ids hidden from the (non-edit) grid
}

const EMPTY_PREFS: QuickLinkPrefs = { order: [], hidden: [] };

const asStringArray = (v: unknown): string[] =>
  Array.isArray(v) ? v.filter((x): x is string => typeof x === "string") : [];

// The first route that isn't a parameterised detail path (e.g. skip "/client/:id").
const resolveMainPath = (config: ModuleConfig): string | null => {
  const routePath = config.routes?.find((r) => r.path && !r.path.includes(":"))?.path;
  if (routePath) return routePath;
  const navPath = config.navigation?.find((n) => n.path && !n.path.includes(":"))?.path;
  return navPath || null;
};

interface ModuleItem {
  config: ModuleConfig;
  path: string;
}

const AdminModuleLinks = () => {
  const { isAuthenticated, user } = useAuth();
  const { colorMode } = useColorMode();

  const [editing, setEditing] = useState(false);
  const [prefs, setPrefs] = useState<QuickLinkPrefs>(EMPTY_PREFS);

  const [saveMutation] = useMutation(SAVE_MY_QUICKLINKS);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Seed from the Client record (myClient.preferences.quickLinks) whenever it
  // loads/changes. This is the source of truth — no localStorage.
  const serverPrefs = useMemo<QuickLinkPrefs>(() => {
    const ql = user?.preferences?.quickLinks;
    return { order: asStringArray(ql?.order), hidden: asStringArray(ql?.hidden) };
  }, [user?.preferences?.quickLinks]);

  useEffect(() => {
    setPrefs(serverPrefs);
  }, [serverPrefs]);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  // Optimistic local update + debounced write to the DB (batches rapid taps).
  const savePrefs = useCallback(
    (next: QuickLinkPrefs) => {
      setPrefs(next);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        saveMutation({ variables: { order: next.order, hidden: next.hidden } }).catch(() => {
          /* transient network/auth error — local state already applied; retried on next edit */
        });
      }, 400);
    },
    [saveMutation]
  );

  // Enabled modules with a resolvable landing path, A–Z by module name.
  // The default order is ALPHABETICAL, not `config.order` — nobody chose
  // `config.order`, it is a launcher display hint, and a 50-item grid in an
  // arbitrary order is unscannable. The person's own order takes over the
  // moment they reorder one card (see `orderedAll`), and Reset returns here.
  // Standard: vault/how-to/how-to-build-new-module.md
  // § "A list of choices is A–Z until the person curates it".
  const baseModules = useMemo<ModuleItem[]>(
    () =>
      allModuleConfigs
        .filter((m) => m.enabled !== false)
        .map((m) => ({ config: m, path: resolveMainPath(m) }))
        .filter((m): m is ModuleItem => !!m.path)
        .sort((a, b) =>
          a.config.name.localeCompare(b.config.name, undefined, {
            sensitivity: "base",
            numeric: true,
          })
        ),
    []
  );

  // Apply the saved order; any module not yet in saved order (e.g. a newly
  // shipped module) falls in at the end A–Z among its peers, so nothing
  // vanishes and nothing the person already placed gets shuffled.
  const orderedAll = useMemo<ModuleItem[]>(() => {
    const byId = new Map(baseModules.map((b) => [b.config.id, b]));
    const ordered: ModuleItem[] = [];
    prefs.order.forEach((id) => {
      const item = byId.get(id);
      if (item) {
        ordered.push(item);
        byId.delete(id);
      }
    });
    baseModules.forEach((b) => {
      if (byId.has(b.config.id)) ordered.push(b);
    });
    return ordered;
  }, [baseModules, prefs.order]);

  const hiddenSet = useMemo(() => new Set(prefs.hidden), [prefs.hidden]);

  const move = useCallback(
    (id: string, dir: -1 | 1) => {
      const ids = orderedAll.map((x) => x.config.id);
      const i = ids.indexOf(id);
      const j = i + dir;
      if (i < 0 || j < 0 || j >= ids.length) return;
      [ids[i], ids[j]] = [ids[j], ids[i]];
      savePrefs({ ...prefs, order: ids });
    },
    [orderedAll, prefs, savePrefs]
  );

  const toggleHidden = useCallback(
    (id: string) => {
      const next = new Set(prefs.hidden);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      savePrefs({ ...prefs, hidden: Array.from(next) });
    },
    [prefs, savePrefs]
  );

  const reset = useCallback(() => savePrefs(EMPTY_PREFS), [savePrefs]);

  // --- gate AFTER all hooks (rules-of-hooks) ---
  const isAdmin = !!user?.permissions?.includes("ADMIN");
  if (!isAuthenticated || !isAdmin) return null;
  if (baseModules.length === 0) return null;

  const items = editing ? orderedAll : orderedAll.filter((x) => !hiddenSet.has(x.config.id));
  // Nothing saved yet → the grid is still the A–Z default, and the edit hint says so.
  const isDefaultOrder = prefs.order.length === 0;

  const cardBg = colorMode === "light" ? "rgba(255,255,255,0.85)" : "rgba(30,30,30,0.7)";
  const cardBorder = colorMode === "light" ? "rgba(0,0,0,0.1)" : "rgba(255,255,255,0.12)";
  const textColor = colorMode === "light" ? "gray.800" : "gray.100";
  const labelColor = colorMode === "light" ? "gray.500" : "gray.400";

  return (
    <Container maxW="1200px" pt={6} pb={2} px={{ base: 4, md: 8 }} position="relative" zIndex={2}>
      <Text
        fontSize="xs"
        fontWeight="bold"
        letterSpacing="wide"
        textTransform="uppercase"
        color={labelColor}
        mb={editing ? 1 : 3}
      >
        Admin — jump to a module{editing ? "" : " (opens in a new tab)"}
      </Text>

      {editing && (
        <Text fontSize="xs" color={labelColor} mb={3}>
          Reorder with ↑ ↓ · tap the eye to hide/show · saved to your account (syncs across devices) ·{" "}
          {isDefaultOrder ? "currently A–Z (default)" : "currently your own order"}
        </Text>
      )}

      {items.length === 0 && !editing ? (
        <Text fontSize="sm" color={labelColor}>
          All modules hidden — tap <b>Edit</b> below to customise your quick links.
        </Text>
      ) : (
        <SimpleGrid columns={{ base: 2, sm: 3, md: 4, lg: 6 }} spacing={3}>
          {items.map(({ config, path }, index) => {
            const isHidden = hiddenSet.has(config.id);
            const accent = config.color || "blue.400";

            const inner = (
              <VStack spacing={1} align="center" opacity={editing && isHidden ? 0.4 : 1}>
                <ModuleGlyph config={config} />
                <Text
                  color={textColor}
                  fontWeight="semibold"
                  fontSize="xs"
                  textAlign="center"
                  noOfLines={2}
                  title={config.name}
                >
                  {config.name}
                </Text>
              </VStack>
            );

            // Shared card chrome
            const cardProps = {
              bg: cardBg,
              border: "1px solid",
              borderColor: cardBorder,
              borderLeft: "3px solid",
              borderLeftColor: accent,
              borderRadius: "xl",
              p: 3,
              minW: 0,
            } as const;

            if (!editing) {
              return (
                <Box
                  key={config.id}
                  as="a"
                  href={path}
                  target="_blank"
                  rel="noopener noreferrer"
                  textDecoration="none"
                  transition="all 0.2s"
                  _hover={{ transform: "translateY(-2px)", boxShadow: "md", borderColor: accent }}
                  {...cardProps}
                >
                  {inner}
                </Box>
              );
            }

            // Edit mode: tile with big tap targets, no navigation.
            return (
              <Box key={config.id} {...cardProps}>
                {inner}
                <HStack spacing={1} justify="center" mt={2}>
                  <IconButton
                    aria-label="Move earlier"
                    icon={<FiChevronUp />}
                    size="sm"
                    variant="ghost"
                    isDisabled={index === 0}
                    onClick={() => move(config.id, -1)}
                  />
                  <IconButton
                    aria-label="Move later"
                    icon={<FiChevronDown />}
                    size="sm"
                    variant="ghost"
                    isDisabled={index === items.length - 1}
                    onClick={() => move(config.id, 1)}
                  />
                  <IconButton
                    aria-label={isHidden ? "Show this module" : "Hide this module"}
                    icon={isHidden ? <FiEyeOff /> : <FiEye />}
                    size="sm"
                    variant="ghost"
                    colorScheme={isHidden ? "gray" : "blue"}
                    onClick={() => toggleHidden(config.id)}
                  />
                </HStack>
              </Box>
            );
          })}
        </SimpleGrid>
      )}

      {/* Controls at the BOTTOM (not top-right). */}
      <Flex justify="center" mt={4} gap={2}>
        {editing && (
          <Button
            size="sm"
            variant="ghost"
            colorScheme="red"
            leftIcon={<FiRotateCcw />}
            onClick={reset}
            title="Clear your saved order and hidden modules — the grid goes back to A–Z with every module showing"
          >
            Reset
          </Button>
        )}
        <Button
          size="sm"
          variant={editing ? "solid" : "outline"}
          colorScheme={editing ? "green" : "gray"}
          leftIcon={editing ? <FiCheck /> : <FiEdit2 />}
          onClick={() => setEditing((e) => !e)}
        >
          {editing ? "Done" : "Edit"}
        </Button>
      </Flex>
    </Container>
  );
};

export default AdminModuleLinks;
